import { useParams } from 'react-router-dom'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation } from 'urql'
import { graphql } from 'app/generated/gql'
import type { UpdateSchedulingConfigurationInput } from 'app/generated/gql/graphql'
import { LocationType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { INSURANCE_LIST } from 'types/InsuranceList'
import { useLocationStore } from 'app/stores/StoreContext'
import { InsurancePicker } from './InsurancePicker'
import { logError } from 'lib/logging'

type InsuranceStatus = `Accepted` | `Rejected` | `Not Set`

export type InsuranceRow = {
  name: string
  status: InsuranceStatus
}

export const booleanOptions = [
  { id: `true`, name: `True`, value: `true` },
  { id: `false`, name: `False`, value: `false` },
]

export const timeIncrementOptions = [
  { id: `10`, name: `10 Minutes`, value: `10` },
  { id: `15`, name: `15 Minutes`, value: `15` },
  { id: `30`, name: `30 Minutes`, value: `30` },
  { id: `60`, name: `60 Minutes`, value: `60` },
]

export const doubleBookingCountOptions = [
  { id: `0`, name: `No limit`, value: `0` },
  { id: `2`, name: `2 per slot`, value: `2` },
  { id: `3`, name: `3 per slot`, value: `3` },
  { id: `4`, name: `4 per slot`, value: `4` },
]

export function getDoubleBookingCountOption(value: number | null | undefined) {
  return doubleBookingCountOptions.find((o) => o.value === String(value ?? 0)) ?? doubleBookingCountOptions[0]
}

export const availabilityModeOptions = [
  { id: `standard`, name: `Standard`, value: `standard` },
  { id: `advanced`, name: `Advanced`, value: `advanced` },
]

export function getAvailabilityModeOption(value: string | undefined) {
  return availabilityModeOptions.find((o) => o.value === value) ?? availabilityModeOptions[0]
}

export function deriveAvailabilityProductFlags(products?: string[]) {
  const has = (p: string) => products?.includes(p) ?? false
  return { hasScheduler: has(`Scheduler`), hasAgent: has(`AI Agent`) || has(`Inbox`) }
}

export const statusOptions = [
  { id: `Accepted`, name: `Accepted`, value: `Accepted` },
  { id: `Rejected`, name: `Rejected`, value: `Rejected` },
  { id: `Not Set`, name: `Not Set`, value: `Not Set` },
]

export function getBooleanOption(value: boolean | undefined) {
  return value ? booleanOptions[0] : booleanOptions[1]
}

export function deriveInsuranceRows(insurancesAccepted: string[], insurancesRejected: string[]): InsuranceRow[] {
  return INSURANCE_LIST.map((name) => {
    let status: InsuranceStatus = `Not Set`
    if (insurancesAccepted.includes(name)) {
      status = `Accepted`
    } else if (insurancesRejected.includes(name)) {
      status = `Rejected`
    }
    return { name, status }
  })
}

export type UseAvailabilitySettingsReturn = {
  schedulingConfiguration: UpdateSchedulingConfigurationInput | null
  setSchedulingConfiguration: React.Dispatch<React.SetStateAction<UpdateSchedulingConfigurationInput | null>>
  locationSettings: { isSchedulerLive: boolean; isAIAgentLive: boolean; availabilityMode: string }
  setLocationSettings: React.Dispatch<
    React.SetStateAction<{ isSchedulerLive: boolean; isAIAgentLive: boolean; availabilityMode: string }>
  >
  isDirtyForm: boolean
  setIsDirtyForm: React.Dispatch<React.SetStateAction<boolean>>
  sortField: `name` | `status`
  sortDirection: `asc` | `desc`
  addInsuranceRef: React.RefObject<HTMLDivElement | null>
  hasScheduler: boolean
  hasAgent: boolean
  isExternalSlots: boolean
  isKolla: boolean
  insurancesAccepted: string[]
  insurancesRejected: string[]
  insuranceRows: InsuranceRow[]
  availableInsurances: { id: string; name: string; value: string }[]
  toggleSort: (field: `name` | `status`) => void
  sortedInsuranceRows: InsuranceRow[]
  handleAddInsurance: (insuranceName: string) => void
  handleInsuranceStatusChange: (insuranceName: string, newStatus: InsuranceStatus) => void
  handleResetInsurance: (insuranceName: string) => void
  handleSaveConfiguration: () => void
  headerButtons: ReactNode
  initialLocation: LocationType
}

const AVAILABILITY_SETTINGS_QUERY = graphql(`
  query AvailabilitySettings($locationId: ID!) {
    schedulingConfiguration(locationId: $locationId) {
      id
      locationId
      allowDoubleBooking
      doubleBookingCount
      insurancesAccepted
      insurancesRejected
      isStateInsuranceAccepted
      granularity
    }
  }
`)

const SAVE_AVAILABILITY_SETTINGS_MUTATION = graphql(`
  mutation SaveAvailabilitySettings(
    $locationId: ID!
    $schedInput: UpdateSchedulingConfigurationInput!
    $locInput: UpdateLocationInput!
  ) {
    updateSchedulingConfiguration(locationId: $locationId, input: $schedInput) {
      id
      allowDoubleBooking
      doubleBookingCount
      insurancesAccepted
      insurancesRejected
      isStateInsuranceAccepted
      granularity
    }
    updateLocation(id: $locationId, input: $locInput) {
      id
      isSchedulerLive
      isAIAgentLive
      availabilityMode
    }
  }
`)

export function useAvailabilitySettings(): UseAvailabilitySettingsReturn {
  const { locationId } = useParams()
  const locationStore = useLocationStore()

  const [initialLocation, setInitialLocation] = useState<LocationType>({} as LocationType)
  const [schedulingConfiguration, setSchedulingConfiguration] = useState<UpdateSchedulingConfigurationInput | null>(
    null,
  )
  const [locationSettings, setLocationSettings] = useState({
    isSchedulerLive: false,
    isAIAgentLive: false,
    availabilityMode: `standard`,
  })

  const [settingsResult] = useQuery({
    query: AVAILABILITY_SETTINGS_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
  })

  useEffect(() => {
    if (settingsResult.data) {
      setSchedulingConfiguration(settingsResult.data.schedulingConfiguration ?? { granularity: 60 })
    }
  }, [settingsResult.data])

  useEffect(() => {
    if (!locationId) return
    locationStore
      .fetchOne(locationId)
      .then((result) => {
        const loc = (result.data as any)?.location ?? result.data
        if (loc) {
          setInitialLocation(loc as LocationType)
          setLocationSettings({
            isSchedulerLive: !!loc.isSchedulerLive,
            isAIAgentLive: !!loc.isAIAgentLive,
            availabilityMode: loc.availabilityMode ?? `standard`,
          })
        }
      })
      .catch((err) => logError(`Error fetching location`, err))
  }, [locationId])

  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const [sortField, setSortField] = useState<`name` | `status`>(`name`)
  const [sortDirection, setSortDirection] = useState<`asc` | `desc`>(`asc`)
  const addInsuranceRef = useRef<HTMLDivElement>(null)

  const { hasScheduler, hasAgent } = deriveAvailabilityProductFlags(initialLocation.products)

  // CareStack/Ascend locations generate slots and enforce booking rules in their PMS, so
  // Trackable's granularity and double-booking settings do not apply to them.
  const isExternalSlots = !!initialLocation.carestackId || !!initialLocation.ascendLocationId

  // Standard/Advanced availability mode only applies to Kolla PMS-connected locations.
  const isKolla = !!initialLocation.kollaConsumerId && !isExternalSlots

  const insurancesAccepted = useMemo(
    () => schedulingConfiguration?.insurancesAccepted ?? [],
    [schedulingConfiguration?.insurancesAccepted],
  )
  const insurancesRejected = useMemo(
    () => schedulingConfiguration?.insurancesRejected ?? [],
    [schedulingConfiguration?.insurancesRejected],
  )

  const insuranceRows = useMemo(
    () => deriveInsuranceRows(insurancesAccepted, insurancesRejected),
    [insurancesAccepted, insurancesRejected],
  )

  const availableInsurances = useMemo(
    () =>
      insuranceRows
        .filter((row) => row.status === `Not Set`)
        .map((row) => ({ id: row.name, name: row.name, value: row.name })),
    [insuranceRows],
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (addInsuranceRef.current && !addInsuranceRef.current.contains(event.target as Node)) {
      }
    }
    document.addEventListener(`mousedown`, handleClickOutside)
    return () => document.removeEventListener(`mousedown`, handleClickOutside)
  }, [])

  const toggleSort = (field: `name` | `status`) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === `asc` ? `desc` : `asc`))
    } else {
      setSortField(field)
      setSortDirection(`asc`)
    }
  }

  const sortedInsuranceRows = useMemo(() => {
    const configured = insuranceRows.filter((row) => row.status !== `Not Set`)
    return configured.sort((a, b) => {
      const valA = sortField === `name` ? a.name : a.status
      const valB = sortField === `name` ? b.name : b.status
      const cmp = valA.localeCompare(valB)
      return sortDirection === `asc` ? cmp : -cmp
    })
  }, [insuranceRows, sortField, sortDirection])

  // Functional update: the header picker is memoized, so reading state from its closure would
  // revert any other unsaved edits made since it rendered.
  const handleInsuranceStatusChange = (insuranceName: string, newStatus: InsuranceStatus) => {
    setSchedulingConfiguration((config) => {
      const accepted = (config?.insurancesAccepted ?? []).filter((name) => name !== insuranceName)
      const rejected = (config?.insurancesRejected ?? []).filter((name) => name !== insuranceName)

      if (newStatus === `Accepted`) {
        accepted.push(insuranceName)
      } else if (newStatus === `Rejected`) {
        rejected.push(insuranceName)
      }

      return { ...config, insurancesAccepted: accepted, insurancesRejected: rejected }
    })
    setIsDirtyForm(true)
  }

  const handleAddInsurance = (insuranceName: string) => {
    handleInsuranceStatusChange(insuranceName, `Accepted`)
  }

  const handleResetInsurance = (insuranceName: string) => {
    handleInsuranceStatusChange(insuranceName, `Not Set`)
  }

  const [, executeSave] = useMutation(SAVE_AVAILABILITY_SETTINGS_MUTATION)

  const handleSaveConfiguration = async () => {
    if (!locationId) return
    if (settingsResult.fetching || settingsResult.error || !schedulingConfiguration) {
      notifyError(`Load the availability settings before saving`)
      return
    }
    const result = await executeSave({
      locationId,
      schedInput: {
        allowDoubleBooking: schedulingConfiguration?.allowDoubleBooking,
        doubleBookingCount: schedulingConfiguration?.doubleBookingCount ?? 0,
        insurancesAccepted: schedulingConfiguration.insurancesAccepted ?? [],
        insurancesRejected: schedulingConfiguration.insurancesRejected ?? [],
        isStateInsuranceAccepted: schedulingConfiguration.isStateInsuranceAccepted ?? false,
        granularity: schedulingConfiguration?.granularity,
      },
      locInput: locationSettings,
    })

    if (result.error) {
      notifyError(`Failed to update configuration`)
    } else {
      if (result.data?.updateSchedulingConfiguration) {
        setSchedulingConfiguration(result.data.updateSchedulingConfiguration)
      }
      notifySuccess(`Configuration saved successfully`)
      setIsDirtyForm(false)
    }
  }

  const headerButtons: ReactNode = useMemo(
    () => (
      <InsurancePicker insurances={availableInsurances.map((insurance) => insurance.name)} onAdd={handleAddInsurance} />
    ),
    [availableInsurances],
  )

  return {
    schedulingConfiguration,
    setSchedulingConfiguration,
    locationSettings,
    setLocationSettings,
    isDirtyForm,
    setIsDirtyForm,
    sortField,
    sortDirection,
    addInsuranceRef,
    hasScheduler,
    hasAgent,
    isExternalSlots,
    isKolla,
    insurancesAccepted,
    insurancesRejected,
    insuranceRows,
    availableInsurances,
    toggleSort,
    sortedInsuranceRows,
    handleAddInsurance,
    handleInsuranceStatusChange,
    handleResetInsurance,
    handleSaveConfiguration,
    headerButtons,
    initialLocation,
  }
}
